import { useState, type ReactNode } from 'react';
import { Boton } from '../componentes/Boton';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { useCopiar, useHecho } from './copiar';

/** RV Copiar: botón secundario que copia el texto y se queda en «hecho» un momento. */
export function BotonCopiar({ texto, etiqueta, hecho, aviso, movil = false }: { texto: string; etiqueta: string; hecho: string; aviso: string; movil?: boolean }) {
    const copiar = useCopiar();
    const [listo, marcar] = useHecho();
    return (
        <Boton
            tipo="secundario"
            icono={listo ? 'check' : 'copy'}
            movil={movil}
            onClick={() => {
                void copiar(texto, aviso).then((ok) => {
                    if (ok) {
                        marcar();
                    }
                });
            }}
        >
            {listo ? hecho : etiqueta}
        </Boton>
    );
}

/** Fila de lista del portal: icono o siglas, nombre con su chip, una línea y lo de la derecha. */
export function Fila({ icono, siglas, titulo, chip, linea, cifra, bajoCifra, cifraApagada = false, accion }: {
    icono?: NombreIcono;
    siglas?: string;
    titulo: string;
    chip?: ReactNode;
    linea?: string;
    cifra?: string;
    bajoCifra?: string;
    cifraApagada?: boolean;
    accion?: ReactNode;
}) {
    return (
        <div className="rv-pt-fila">
            {siglas !== undefined && <span className="rv-pt-fila__siglas" aria-hidden="true">{siglas}</span>}
            {icono !== undefined && <Icono nombre={icono} className="rv-pt-fila__icono" />}
            <span className="rv-pt-fila__textos">
                <span className="rv-pt-fila__titulo">
                    <span>{titulo}</span>
                    {chip}
                </span>
                {linea !== undefined && linea !== '' && <span className="rv-pt-fila__linea">{linea}</span>}
            </span>
            {cifra !== undefined && (
                <span className="rv-pt-fila__cifra">
                    <strong className="rv-num" style={cifraApagada ? { color: 'var(--rv-fg-3)' } : undefined}>{cifra}</strong>
                    {bajoCifra !== undefined && <span>{bajoCifra}</span>}
                </span>
            )}
            {accion}
        </div>
    );
}

/** Barras horizontales (la comisión de cada negocio): cada valor de la API sobre el mayor, solo para dibujar. */
export function BarrasH({ filas, etiqueta }: { filas: readonly { clave: string; nombre: string; valor: number; cifra: string; detalle?: string }[]; etiqueta: string }) {
    const maximo = Math.max(0, ...filas.map((f) => f.valor));
    return (
        <ul className="rv-pt-barras-h" aria-label={etiqueta}>
            {filas.map((f) => (
                <li key={f.clave} title={f.detalle}>
                    <span className="rv-pt-barras-h__nombre">{f.nombre}</span>
                    <strong className="rv-num">{f.cifra}</strong>
                    <i style={{ width: `${String(maximo > 0 ? (f.valor / maximo) * 100 : 0)}%` }} />
                </li>
            ))}
        </ul>
    );
}

/**
 * Barras por mes (Tu comisión por mes): una por liquidación con su total de la API; la del mes elegido en rojo. Tocar una
 * elige ese mes. «Ver como tabla» enseña los mismos datos en una tabla.
 */
export function BarrasMes({ barras, elegida, onElegir }: { barras: readonly { clave: string; corta: string; larga: string; valor: number; cifra: string }[]; elegida: string; onElegir: (clave: string) => void }) {
    const [tabla, setTabla] = useState(false);
    const maximo = Math.max(0, ...barras.map((b) => b.valor));
    return (
        <div className="rv-pt-grafico">
            {tabla ? (
                <table className="rv-pt-tabla">
                    <thead>
                        <tr>
                            <th scope="col">Mes</th>
                            <th scope="col">Tu comisión</th>
                        </tr>
                    </thead>
                    <tbody>
                        {barras.map((b) => (
                            <tr key={b.clave} aria-current={b.clave === elegida ? 'true' : undefined}>
                                <td>{b.larga}</td>
                                <td className="rv-num">{b.cifra}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="rv-pt-barras" role="list">
                    {barras.map((b) => (
                        <button
                            key={b.clave}
                            type="button"
                            role="listitem"
                            className="rv-pt-barras__b"
                            aria-current={b.clave === elegida ? 'true' : undefined}
                            aria-label={`${b.larga}: ${b.cifra}`}
                            onClick={() => {
                                onElegir(b.clave);
                            }}
                        >
                            <span className="rv-pt-barras__pista">
                                <i style={{ height: `${String(maximo > 0 ? (b.valor / maximo) * 100 : 0)}%` }} />
                            </span>
                            <span className="rv-pt-barras__mes">{b.corta}</span>
                        </button>
                    ))}
                </div>
            )}
            <button
                type="button"
                className="rv-pt-enlace"
                onClick={() => {
                    setTabla(!tabla);
                }}
            >
                <Icono nombre={tabla ? 'chart-column' : 'table'} />
                <span>{tabla ? 'Ver como gráfico' : 'Ver como tabla'}</span>
            </button>
        </div>
    );
}
